/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
/* eslint-disable react/no-danger */
import React, { useEffect, useMemo, useRef } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Avatar, Box, Card, Checkbox, CircularProgress, Divider, FormControlLabel, Grow, IconButton, ListItem, Tooltip, useMediaQuery, useTheme
} from '@mui/material';
import {
  GoogleMap,
  useJsApiLoader
} from '@react-google-maps/api';
import { useParams } from 'react-router-dom';

import { useAuth } from '../../contexts/auth';
import AdvancedMarker from '../../components/maps/AdvancedMarker';
import { getGoogleMapOptions, GOOGLE_MAPS_API_KEY } from '../../config/googleMaps';
import subsidiaryService from '../../services/subsidiaryService';
import Loading from '../../components/loading';
import driverService from '../../services/driverService';
import { phoneNumber, toBrazilDateTime } from '../../components/mask';
import subsidiaryMarker from '../../assets/images/subsidiary-marker.png';
import helm from '../../assets/images/helm.png';
import helmGreen from '../../assets/images/helm-green.png';
import helmRed from '../../assets/images/helm-red.png';
import StatusCallHistory from '../../models/enums/statusCallHistory';
import { socket } from '../../services/socketioService';
import SmoothMarker from './components/smoothMarker';
import callService from '../../services/callService';
import {
  createCanvasHeatmapOverlay,
  parseHeatmapRows,
  HeatmapPoint
} from './components/canvasHeatmap';

interface Row {
  id: string;
  description: string;
  category: string;
  html: string;
  photoURL: string;
  phoneNumber: string | null;
  active: boolean;
  busy: boolean;
  lat: number | null;
  lng: number | null;
  updatedAt: Date | null;
  isLink: boolean;
  call: any,
  stationary: boolean
}

interface OwnProps {
  subsidiaryId?: string | null;
  title?: string;
}

interface RouteParams {
  id?: string;
}

const SNAPSHOT_SYNC_MS = 60 * 1000;
const STALE_CLEANUP_MS = 30 * 1000;
const HEATMAP_SYNC_MS = 60 * 1000;
const HEATMAP_HOURS = 2;
const GOOGLE_MAPS_LIBRARIES: ('places' | 'marker')[] = ['places', 'marker'];
const mapContainerStyle = { width: '100%', height: '600px' };
const PANEL_TRANSITION_MS = 450;
const PANEL_WIDTH_SM = 320;
const PANEL_WIDTH_MD = 360;
const HEATMAP_TOOLTIP = 'Exibe as regiões com maior volume de pedidos partindo dos estabelecimentos (origem), nas últimas 2 horas — não o local de entrega. Ajuda a identificar onde prospectar novos estabelecimentos na cidade.';

export default function page(props: OwnProps) {
  const { user, subsidiaryId: authSubsidiaryId, subsidiaryGuid } = useAuth();
  const params = useParams<RouteParams>();
  const subsidiaryId = props.subsidiaryId
    || params.id
    || authSubsidiaryId
    || subsidiaryGuid
    || null;
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'drivers-realtime-map-sdk',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES
  });
  const muiTheme = useTheme();
  const isCompactHeader = useMediaQuery(muiTheme.breakpoints.down('md'));

  const [isLoading, setIsLoading] = React.useState(true);
  const [subsidiaries, setSubsidiaries] = React.useState<Row[]>([]);
  const [driverSelectedId, setDriverSelectedId] = React.useState<string | null>(null);
  const [zoom, setZoom] = React.useState(4.5);
  const [center, setCenter] = React.useState({ lat: -11.840096772124648, lng: -51.95084271385832 });
  const [drivers, setDrivers] = React.useState<any[]>([]);
  const [showHeatmap, setShowHeatmap] = React.useState(true);
  const [heatmapPoints, setHeatmapPoints] = React.useState<HeatmapPoint[]>([]);
  const [isHeatmapLoading, setIsHeatmapLoading] = React.useState(false);
  const [driversPanelOpen, setDriversPanelOpen] = React.useState(true);
  const [mapReady, setMapReady] = React.useState(false);
  const driversOnMapRef = useRef<any[]>([]);
  const mapOriginRef = useRef({ lat: 0, lng: 0 });
  const mapRef = useRef<google.maps.Map | null>(null);
  const heatmapLayerRef = useRef<ReturnType<typeof createCanvasHeatmapOverlay> | null>(null);

  const divStyle = { color: 'white', backgroundColor: '#d1d2db' };

  const mapIcons = useMemo(() => {
    if (!isLoaded || typeof google === 'undefined' || !google.maps?.Size || !google.maps?.Point) return null;
    return {
      subsidiary: {
        url: subsidiaryMarker,
        scaledSize: new google.maps.Size(64, 64),
        anchor: new google.maps.Point(32, 64)
      },
      available: {
        url: helm,
        scaledSize: new google.maps.Size(28, 28),
        anchor: new google.maps.Point(14, 14)
      },
      accepted: {
        url: helmGreen,
        scaledSize: new google.maps.Size(26, 26),
        anchor: new google.maps.Point(13, 13)
      },
      busy: {
        url: helmRed,
        scaledSize: new google.maps.Size(26, 26),
        anchor: new google.maps.Point(13, 13)
      }
    };
  }, [isLoaded]);

  const mapOptions = useMemo(() => {
    if (!isLoaded || typeof google === 'undefined') return undefined;
    return getGoogleMapOptions({
      mapTypeControl: true,
      fullscreenControl: true,
      streetViewControl: false,
      zoomControl: true,
      clickableIcons: false,
      gestureHandling: 'greedy'
    });
  }, [isLoaded]);

  const publishDrivers = React.useCallback((list: any[]) => {
    try {
      const fresh = driverService.removeFromOnlineDriversList(list);
      const sorted = _.sortBy(fresh, (x) => x.description ?? '');
      driversOnMapRef.current = sorted;
      setDrivers(sorted);
    } catch {
      driversOnMapRef.current = [];
      setDrivers([]);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    let staleInterval: ReturnType<typeof setInterval> | undefined;
    let snapshotInterval: ReturnType<typeof setInterval> | undefined;
    const allDriversChannel = `${subsidiaryId}:alldrivers`;
    const driversDeleteChannel = `${subsidiaryId}:drivers:delete`;

    const applySocketUpdate = (payload: any) => {
      const listUpdated = driverService.updateOnlineDriversList(
        driversOnMapRef.current,
        payload
      );
      publishDrivers(listUpdated);
    };

    const loadSnapshot = async () => {
      try {
        const listFromRedis = await driverService.getOnlineFromRedis(
          subsidiaryId || null,
          user?.uid || null,
          mapOriginRef.current.lat,
          mapOriginRef.current.lng
        );
        console.log('listFromRedis', listFromRedis);
        if (cancelled || listFromRedis == null) return;
        const preparedList = driverService.prepareDriverList(listFromRedis);
        publishDrivers(driverService.mergeOnlineSnapshot(driversOnMapRef.current, preparedList));
      } catch {
        if (!cancelled) publishDrivers(driversOnMapRef.current);
      }
    };

    const loadSubsidiary = async () => {
      if (subsidiaryId) {
        const data = await subsidiaryService.getByIdAsync(subsidiaryId);
        const location = data?.address?.location;
        const lat = location?.latitude ?? 0;
        const lng = location?.longitude ?? 0;
        mapOriginRef.current = { lat, lng };
        if (location && !cancelled) {
          setCenter({ lat, lng });
          setZoom(13);
          setSubsidiaries([{
            id: data.id,
            description: data.description,
            category: '',
            html: '',
            active: true,
            busy: false,
            lat,
            lng,
            phoneNumber: null,
            photoURL: '',
            updatedAt: null,
            isLink: false,
            call: null,
            stationary: false
          }]);
        }
        return;
      }

      const subs = await subsidiaryService.getByPaginationAsync(1, 200);
      if (!cancelled) {
        setSubsidiaries(subs.data.filter((x: any) => x.active));
      }
    };

    const start = async () => {
      setIsLoading(true);
      await loadSubsidiary();
      if (cancelled) return;
      await loadSnapshot();
      if (cancelled) return;

      socket.on(allDriversChannel, applySocketUpdate);
      socket.on(driversDeleteChannel, applySocketUpdate);
      socket.on('connect', loadSnapshot);
      if (!socket.connected) {
        socket.connect();
      }

      staleInterval = setInterval(() => {
        publishDrivers(driverService.removeFromOnlineDriversList(driversOnMapRef.current));
      }, STALE_CLEANUP_MS);
      snapshotInterval = setInterval(() => {
        loadSnapshot();
      }, SNAPSHOT_SYNC_MS);

      setIsLoading(false);
    };

    start();

    return () => {
      cancelled = true;
      socket.off(allDriversChannel, applySocketUpdate);
      socket.off(driversDeleteChannel, applySocketUpdate);
      socket.off('connect', loadSnapshot);
      if (staleInterval) clearInterval(staleInterval);
      if (snapshotInterval) clearInterval(snapshotInterval);
    };
  }, [subsidiaryId, user?.uid, publishDrivers]);

  useEffect(() => {
    if (!showHeatmap) {
      setHeatmapPoints([]);
      setIsHeatmapLoading(false);
      return undefined;
    }

    if (!subsidiaryId) {
      setHeatmapPoints([]);
      setIsHeatmapLoading(false);
      return undefined;
    }

    let cancelled = false;

    const loadHeatmap = async () => {
      setIsHeatmapLoading(true);
      try {
        const rows = await callService.getHeatmap(subsidiaryId, HEATMAP_HOURS);
        if (cancelled) return;
        setHeatmapPoints(parseHeatmapRows(rows));
      } finally {
        if (!cancelled) setIsHeatmapLoading(false);
      }
    };

    loadHeatmap();
    const heatmapInterval = setInterval(loadHeatmap, HEATMAP_SYNC_MS);

    return () => {
      cancelled = true;
      clearInterval(heatmapInterval);
    };
  }, [subsidiaryId, showHeatmap]);

  useEffect(() => {
    const cleanup = () => {
      if (heatmapLayerRef.current) {
        heatmapLayerRef.current.setMap(null);
        heatmapLayerRef.current = null;
      }
    };

    if (!mapReady || !mapRef.current || !isLoaded || typeof google === 'undefined') {
      return cleanup;
    }

    if (!showHeatmap || heatmapPoints.length === 0) {
      cleanup();
      return cleanup;
    }

    try {
      if (heatmapLayerRef.current) {
        heatmapLayerRef.current.setPoints(heatmapPoints);
        return undefined;
      }

      heatmapLayerRef.current = createCanvasHeatmapOverlay(mapRef.current, heatmapPoints, {
        radius: 56,
        opacity: 0.5
      });
    } catch {
      return cleanup;
    }

    return cleanup;
  }, [heatmapPoints, showHeatmap, isLoaded, mapReady]);

  useEffect(() => {
    if (!mapReady || !mapRef.current || typeof google === 'undefined') return undefined;

    const timer = setTimeout(() => {
      google.maps.event.trigger(mapRef.current!, 'resize');
    }, PANEL_TRANSITION_MS);

    return () => clearTimeout(timer);
  }, [driversPanelOpen, mapReady]);

  const getDriverIcon = (driver: any) => {
    if (!mapIcons) return undefined;
    if (driver.call?.id === null) return mapIcons.available;
    if (driver.call?.status === StatusCallHistory.Accepted) return mapIcons.accepted;
    return mapIcons.busy;
  };

  const handleMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;
    setMapReady(true);
  };

  const handleMapUnmount = () => {
    mapRef.current = null;
    setMapReady(false);
  };

  const heatmapLabelText = !subsidiaryId
    ? (isCompactHeader ? 'Mapa de calor (selecione franquia)' : 'Mapa de calor por origem (selecione uma franquia)')
    : (isCompactHeader ? 'Mapa de calor (origem dos pedidos)' : 'Exibir mapa de calor pela origem dos pedidos');

  const heatmapLabel = subsidiaryId ? (
    <Tooltip title={HEATMAP_TOOLTIP} arrow>
      <span>{heatmapLabelText}</span>
    </Tooltip>
  ) : heatmapLabelText;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 3,
        alignItems: 'flex-start'
      }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          width: '100%',
          transition: (t) => t.transitions.create(['flex', 'width'], {
            duration: PANEL_TRANSITION_MS,
            easing: t.transitions.easing.easeInOut
          })
        }}
      >
        <Card className="card-box mb-4">
          <Box
            className="card-header py-3"
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: { xs: 1.5, md: 0 }
            }}
          >
            <div className="card-header--title font-size-lg">
              {props.title ?? 'Condutores em tempo real'}
            </div>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                width: { xs: '100%', md: 'auto' },
                maxWidth: { xs: '100%', md: '55%' },
                gap: 0.5
              }}
            >
              <FormControlLabel
                sx={{
                  m: 0,
                  alignItems: 'center',
                  maxWidth: '100%',
                  '& .MuiFormControlLabel-label': {
                    fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                    lineHeight: 1.35,
                    whiteSpace: { xs: 'normal', lg: 'nowrap' }
                  }
                }}
                control={(
                  <Checkbox
                    checked={showHeatmap}
                    onChange={(e) => setShowHeatmap(e.target.checked)}
                    size="small"
                    color="primary"
                    disabled={!subsidiaryId}
                    sx={{ p: 0.5 }}
                  />
                )}
                label={heatmapLabel}
              />
              <Grow
                in={!driversPanelOpen}
                timeout={{ enter: PANEL_TRANSITION_MS, exit: 200 }}
                style={{ transformOrigin: 'center left' }}
                unmountOnExit
              >
                <Tooltip title="Exibir últimas atualizações">
                  <IconButton
                    size="small"
                    onClick={() => setDriversPanelOpen(true)}
                    aria-label="Exibir últimas atualizações"
                    sx={{
                      ml: 0.5,
                      transition: 'transform 0.25s ease',
                      '&:hover': { transform: 'translateX(-3px) scale(1.08)' }
                    }}
                  >
                    <FontAwesomeIcon icon={['fas', 'chevron-left']} />
                  </IconButton>
                </Tooltip>
              </Grow>
              {isHeatmapLoading && showHeatmap && subsidiaryId && (
                <CircularProgress
                  size={18}
                  color="primary"
                  title="Carregando mapa de calor"
                  style={{ marginLeft: 4 }}
                />
              )}
            </Box>
          </Box>
          <div className="w-100" style={{ height: '600px' }}>
            {loadError && (
              <div className="d-flex align-items-center justify-content-center h-100 text-black-50">
                Não foi possível carregar o Google Maps. Verifique REACT_APP_GOOGLE_MAPS_API_KEY.
              </div>
            )}
            {!loadError && (isLoading || !isLoaded)
              ? <Loading />
              : (
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={center}
                  zoom={zoom}
                  options={mapOptions}
                  onLoad={handleMapLoad}
                  onUnmount={handleMapUnmount}
                >
                  {mapReady && mapRef.current && subsidiaries.map((s: Row) => (
                    <AdvancedMarker
                      key={s.id}
                      map={mapRef.current}
                      position={{ lat: s.lat ?? 0, lng: s.lng ?? 0 }}
                      icon={mapIcons?.subsidiary}
                      title="Filial"
                      zIndex={0}
                    />
                  ))}
                  {mapReady && mapRef.current && drivers?.map((x) => (
                    <SmoothMarker
                      key={x.id}
                      map={mapRef.current}
                      position={{ lat: x.lat ?? 0, lng: x.lng ?? 0 }}
                      icon={getDriverIcon(x)}
                      title={x.description}
                      zIndex={1}
                      stationary={x.stationary}
                      showInfo={driverSelectedId === x.id}
                      onClick={() => setDriverSelectedId(x.id)}
                      onCloseInfo={() => setDriverSelectedId(null)}
                    >
                      <div className="d-flex align-items-center">
                        <div className="avatar-icon-wrapper avatar-icon-lg  mr-2">
                          <div className="avatar-icon">
                            <img alt={x.phoneNumber ?? ''} src={x.photoURL} />
                          </div>
                        </div>
                        <div>
                          <span className="font-weight-bold text-black" title="">
                            {x.isLink
                              ? <a target="_blank" href={`/drivers/${x.id}`} rel="noreferrer">{x.description}</a>
                              : <p>{x.description}</p>}
                          </span>
                          <span className="text-black-50 d-block">
                            <div dangerouslySetInnerHTML={{ __html: x.html }} />
                          </span>
                        </div>
                      </div>
                    </SmoothMarker>
                  ))}
                </GoogleMap>
              )}
          </div>
          <div className="d-block text-black-50 font-size-sm ml-2 mr-1">
            <p>
              É importante estar ciente de que a confiabilidade das informações exibidas no mapa e na lista podem ser afetadas pela qualidade do sinal de internet do celular do condutor.
              Sinais móveis variam em força e estabilidade dependendo de diversos fatores, como localização geográfica, congestionamento da rede e condições climáticas.
            </p>
          </div>
        </Card>
      </Box>
      <Box
        sx={{
          overflow: 'hidden',
          flexShrink: 0,
          width: {
            xs: driversPanelOpen ? '100%' : 0,
            sm: driversPanelOpen ? PANEL_WIDTH_SM : 0,
            md: driversPanelOpen ? PANEL_WIDTH_MD : 0
          },
          maxHeight: {
            xs: driversPanelOpen ? 640 : 0,
            sm: 'none'
          },
          opacity: driversPanelOpen ? 1 : 0,
          transform: driversPanelOpen ? 'translateX(0)' : 'translateX(28px)',
          pointerEvents: driversPanelOpen ? 'auto' : 'none',
          transition: (t) => t.transitions.create(
            ['width', 'max-height', 'opacity', 'transform'],
            {
              duration: PANEL_TRANSITION_MS,
              easing: t.transitions.easing.easeInOut
            }
          )
        }}
      >
        <Box
          sx={{
            width: { xs: '100%', sm: PANEL_WIDTH_SM, md: PANEL_WIDTH_MD },
            transform: driversPanelOpen ? 'translateX(0)' : 'translateX(12px)',
            transition: (t) => t.transitions.create(['transform'], {
              duration: PANEL_TRANSITION_MS,
              easing: t.transitions.easing.easeInOut
            })
          }}
        >
          <Card className="card-box">
            <div className="card-header py-3 d-flex align-items-center justify-content-between">
              <div className="card-header--title font-size-lg">Últimas atualizações</div>
              <Tooltip title="Recolher painel">
                <IconButton
                  size="small"
                  onClick={() => setDriversPanelOpen(false)}
                  aria-label="Recolher painel"
                  sx={{
                    transition: 'transform 0.25s ease',
                    '&:hover': { transform: 'translateX(3px) scale(1.08)' }
                  }}
                >
                  <FontAwesomeIcon icon={['fas', 'chevron-right']} />
                </IconButton>
              </Tooltip>
            </div>
            <div className="scroll-area shadow-overflow" style={{ height: '600px' }}>
              <PerfectScrollbar>
                {drivers?.map((d, i) => (
                  <div key={d.id}>
                    <ListItem button className="align-box-row" style={driverSelectedId === d.id ? divStyle : undefined}>
                      <div className="align-box-row w-100">
                        <Avatar alt="" src={typeof d.photoURL === 'string' ? d.photoURL : ''} className="mr-2" title={phoneNumber(d.phoneNumber != null ? String(d.phoneNumber) : null)} />
                        <div>
                          <span className={`font-weight ${d.call?.id === null ? 'text-black' : (d.call?.status === StatusCallHistory.Accepted ? 'text-success' : 'text-danger')}`} title={d.busy ? 'Ocupado' : 'Disponível'}>
                            {d.description ?? ''}
                          </span>
                          <span className={`d-block ${d.call?.id === null ? 'text-black' : (d.call?.status === StatusCallHistory.Accepted ? 'text-success' : 'text-danger')}`} title={d.busy ? 'Ocupado' : 'Disponível'}>
                            {d.category ?? ''}
                          </span>
                          <span className={`d-block ${driverSelectedId === d.id ? '' : 'text-black-50'}`} title="Última atualização">
                            {toBrazilDateTime(d.updatedAt)}
                          </span>
                          <span className="d-block text-black-50" title="Status">
                            {d.stationary ? 'Estacionado' : 'Em movimento'}
                          </span>
                        </div>
                        <div className="ml-auto card-hover-indicator align-self-center">
                          <FontAwesomeIcon icon={['fas', 'chevron-right']} className="font-size-md" />
                        </div>
                      </div>
                    </ListItem>
                    {drivers.length - 1 !== i && <Divider />}
                  </div>
                ))}
              </PerfectScrollbar>
            </div>
          </Card>
        </Box>
      </Box>
    </Box>
  );
}
